import { CloseButton } from "@components/buttons/close";
import { configTooltipStyle } from "@components/buttons/information/config";
import {
  SHBox,
  SHHtmlBlock,
  SHHtmlBlockProps,
  SHPopper,
  SHStack,
} from "@components/design-systems";
import { IS_FIREFOX } from "@components/design-systems/sh-rich-text-editor-v2/shared/environment";
import { InfoSVG, TooltipAllowSVG } from "@components/svgs";
import { useClickAwayRefs } from "@hooks/useClickAwayRefs";
import { IconButton, PopperPlacementType, useTheme } from "@mui/material";
import shadows from "@mui/material/styles/shadows";
import { hexToRGBA } from "@utils";
import React, { useRef, useState } from "react";

type SHRichTextTooltipProps = {
  htmlTitle?: string | null | undefined;
  htmlContent?: string | null;
  placement?: PopperPlacementType;
  customIcon?: JSX.Element;
  displayCloseButton?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Allow dynamic props
  [key: string]: any;
} & SHHtmlBlockProps;

export const SHRichTextTooltip = ({
  htmlTitle,
  htmlContent,
  placement = "top",
  customIcon,
  maxWidth = 600,
  maxHeight = 300,
  displayCloseButton = false,
  ...others
}: SHRichTextTooltipProps) => {
  const { palette } = useTheme();

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const tooltipConfig = configTooltipStyle[placement];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  useClickAwayRefs([buttonRef, wrapperRef], () => {
    setOpen(false);
  });

  return (
    <>
      {customIcon ? (
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
          disableRipple
          sx={{
            p: 0,
          }}
        >
          {customIcon}
        </IconButton>
      ) : (
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
          disableRipple
          sx={{
            width: "16px",
            height: "16px",
            margin: "0px 0px 0px 0px !important",
            p: 0,
            borderStyle: "solid",
            borderWidth: "1px",
            backgroundColor: "transparent",
            borderColor: palette.text.primary,
            "&:hover": {
              borderColor: palette.primary.main,
              boxShadow: "none",
              "&>svg>path": {
                fill: palette.primary.main,
              },
            },
            "&:active": {
              backgroundColor: palette.primary.main,
              borderColor: palette.primary.dark,
              "&>svg>path": {
                fill: palette.common.white,
              },
            },
            "&:disabled": {
              border: `1px solid ${palette.text.disabled}`,

              "&>svg>path": {
                fill: palette.text.disabled,
              },
            },
          }}
        >
          <InfoSVG />
        </IconButton>
      )}

      <SHPopper
        anchorEl={buttonRef.current}
        open={open}
        placement={placement}
        popperOptions={{ modifiers: [{ name: "flip", enabled: false }] }}
        sx={{ zIndex: 1400 }}
      >
        <SHBox pb={1}>
          <SHStack
            ref={wrapperRef}
            sx={{
              p: IS_FIREFOX ? "16px" : "16px 16px 0px 16px",
              backgroundColor: hexToRGBA(palette.common.white, 0.85),
              border: "1px solid #E3E3E3",
              backdropFilter: "blur(2px)",
              borderRadius: "3px",
              boxShadow: shadows[1],
              position: "relative",
              gap: 1,
              maxWidth: maxWidth,
              maxHeight: maxHeight,
              width: "fit-content",
            }}
          >
            {displayCloseButton && (
              <SHBox position={"absolute"} top={0.5} right={4}>
                <CloseButton onClick={() => setOpen(false)} />
              </SHBox>
            )}

            {htmlTitle && (
              <SHHtmlBlock
                content={htmlTitle}
                fontWeight={"700 !important"}
                textAlign={"left"}
                pr={1}
              />
            )}

            <SHHtmlBlock
              {...others}
              content={htmlContent}
              overflow={"scroll"}
              textAlign={"left"}
              pr={1}
            />
          </SHStack>
          <SHBox sx={tooltipConfig?.sx}>
            <TooltipAllowSVG transform={tooltipConfig?.transform} />
          </SHBox>
        </SHBox>
      </SHPopper>
    </>
  );
};
